import React, { createContext, useContext, useState, useCallback } from "react";

const LayoutContext = createContext();

// Layout configurations
export const LAYOUTS = {
  SINGLE: "single", // Full screen single panel
  SPLIT_H: "split-horizontal", // 2 panels left/right
  SPLIT_V: "split-vertical", // 2 panels top/bottom
  TRIPLE_L: "triple-left", // 1 large left, 2 stacked right
  TRIPLE_R: "triple-right", // 2 stacked left, 1 large right
  TRIPLE_T: "triple-top", // 1 large top, 2 side-by-side bottom
  TRIPLE_B: "triple-bottom", // 2 side-by-side top, 1 large bottom
  QUAD: "quad", // 4 equal quadrants
};

// Panel types that can be placed in quadrants
export const PANEL_TYPES = {
  CHAT: "chat",
  DOCUMENT: "document",
  QUIZ: "quiz",
  FLASHCARDS: "flashcards",
  NOTES: "notes",
  EMPTY: "empty",
};

// Panel positions for each layout
const LAYOUT_POSITIONS = {
  [LAYOUTS.SINGLE]: ["main"],
  [LAYOUTS.SPLIT_H]: ["left", "right"],
  [LAYOUTS.SPLIT_V]: ["top", "bottom"],
  [LAYOUTS.TRIPLE_L]: ["left", "top-right", "bottom-right"],
  [LAYOUTS.TRIPLE_R]: ["top-left", "bottom-left", "right"],
  [LAYOUTS.TRIPLE_T]: ["top", "bottom-left", "bottom-right"],
  [LAYOUTS.TRIPLE_B]: ["top-left", "top-right", "bottom"],
  [LAYOUTS.QUAD]: ["top-left", "top-right", "bottom-left", "bottom-right"],
};

// Default panel configuration
const createDefaultPanel = (type = PANEL_TYPES.EMPTY) => ({
  type,
  content: null,
  title: "",
  chatId:
    type === PANEL_TYPES.CHAT
      ? `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      : null,
  messages: [],
});

export const LayoutProvider = ({ children }) => {
  const [layout, setLayout] = useState(LAYOUTS.SINGLE);
  const [panels, setPanels] = useState({
    main: createDefaultPanel(PANEL_TYPES.CHAT),
  });
  const [isLayoutMode, setIsLayoutMode] = useState(false);
  const [activePanelId, setActivePanelId] = useState("main");

  // Get available positions for current layout
  const getPositions = useCallback(() => {
    return LAYOUT_POSITIONS[layout] || ["main"];
  }, [layout]);

  // Change layout and initialize panels
  const changeLayout = useCallback(
    (newLayout) => {
      const positions = LAYOUT_POSITIONS[newLayout];
      const existingPanelsArray = Object.entries(panels);
      const newPanels = {};

      positions.forEach((pos, index) => {
        if (existingPanelsArray[index]) {
          // Preserve existing panel
          newPanels[pos] = existingPanelsArray[index][1];
        } else {
          // Create new empty panel - first additional panel gets chat by default
          newPanels[pos] = createDefaultPanel(
            index === 0 ? PANEL_TYPES.CHAT : PANEL_TYPES.EMPTY
          );
        }
      });

      setPanels(newPanels);
      setLayout(newLayout);
      setActivePanelId(positions[0]);
    },
    [panels]
  );

  // Set panel content type
  const setPanelContent = useCallback(
    (position, type, content = null, title = "") => {
      setPanels((prev) => ({
        ...prev,
        [position]: {
          ...prev[position],
          type,
          content,
          title,
          chatId:
            type === PANEL_TYPES.CHAT
              ? `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
              : prev[position]?.chatId,
          messages: type === PANEL_TYPES.CHAT ? [] : prev[position]?.messages,
        },
      }));
    },
    []
  );

  // Update panel messages (for chat panels)
  const updatePanelMessages = useCallback((position, messages) => {
    setPanels((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        messages,
      },
    }));
  }, []);

  // Add message to panel
  const addMessageToPanel = useCallback((position, message) => {
    setPanels((prev) => ({
      ...prev,
      [position]: {
        ...prev[position],
        messages: [...(prev[position]?.messages || []), message],
      },
    }));
  }, []);

  // Clear panel
  const clearPanel = useCallback((position) => {
    setPanels((prev) => ({
      ...prev,
      [position]: createDefaultPanel(PANEL_TYPES.EMPTY),
    }));
  }, []);

  // Swap two panels
  const swapPanels = useCallback((pos1, pos2) => {
    setPanels((prev) => {
      const newPanels = { ...prev };
      const temp = newPanels[pos1];
      newPanels[pos1] = newPanels[pos2];
      newPanels[pos2] = temp;
      return newPanels;
    });
  }, []);

  // Toggle layout editing mode
  const toggleLayoutMode = useCallback(() => {
    setIsLayoutMode((prev) => !prev);
  }, []);

  // Exit layout mode
  const exitLayoutMode = useCallback(() => {
    setIsLayoutMode(false);
  }, []);

  // Reset to single layout
  const resetLayout = useCallback(() => {
    setLayout(LAYOUTS.SINGLE);
    setPanels({
      main: createDefaultPanel(PANEL_TYPES.CHAT),
    });
    setActivePanelId("main");
  }, []);

  const value = {
    layout,
    panels,
    isLayoutMode,
    activePanelId,
    setActivePanelId,
    getPositions,
    changeLayout,
    setPanelContent,
    updatePanelMessages,
    addMessageToPanel,
    clearPanel,
    swapPanels,
    toggleLayoutMode,
    exitLayoutMode,
    resetLayout,
    LAYOUTS,
    PANEL_TYPES,
  };

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

export default LayoutContext;
